import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listEmergencyVisits } from "../../../api/emergency";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import { getObjectsWithParams } from "../../../api/Api";
import EmergencyOverview from "./EmergencyOverview";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Autocomplete,
  Grid,
  Divider,
  TableContainer,
  FormControl,
  Select,
  Pagination,
  CircularProgress,
  Alert,
} from "@mui/material";

export default function EmergencyList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params, setParams] = useState({});
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [attendingOptions, setAttendingOptions] = useState([]);
  const [attendingValue, setAttendingValue] = useState(null);
  const [attendingInput, setAttendingInput] = useState("");
  
  // Debounced search term
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, refetch, isFetching, error } = useQuery({
    queryKey: ["emergency-list", params, page, rowsPerPage, debouncedSearch],
    queryFn: () =>
      listEmergencyVisits(
        { ...params, search: debouncedSearch || undefined, page: page + 1, page_size: rowsPerPage },
        token
      ),
  });

  // Fetch attending doctor options
  useEffect(() => {
    const fetchAttending = async () => {
      try {
        if (!attendingInput || attendingInput.trim().length < 2) return;
        const res = await getObjectsWithParams(
          "users",
          { search: attendingInput },
          token
        );
        const rows = res?.results || res || [];
        setAttendingOptions(rows);
      } catch (e) {
        // ignore
      }
    };
    fetchAttending();
  }, [attendingInput, token]);

  // Update params when filters change
  useEffect(() => {
    const newParams = { ...params };
    if (dateFrom) newParams.arrival_time__gte = dateFrom;
    if (dateTo) newParams.arrival_time__lte = dateTo;
    if (attendingValue) newParams.attending_doctor = attendingValue.id;
    setParams(newParams);
  }, [dateFrom, dateTo, attendingValue]);

  const rows = data?.results || data || [];
  const total = data?.count || rows.length;

  const triageLabel = (v) => ({ 1: "Red", 2: "Yellow", 3: "Green" }[v] || "-");
  const dispositionLabel = (v) =>
    ({
      1: "Discharge",
      2: "Admit IPD",
      3: "Admit ICU",
      4: "Referral",
      5: "Death",
    }[v] || "-");

  return (
    <Grid container maxWidth="xl" mb={3}>
      <Grid container gap={1} mt={2} justifyContent={"center"}>
        <Grid item xs={12} md={12} lg={4.5}>
          <Grid container>
            <EmergencyOverview />
          </Grid>
        </Grid>
        <Grid item xs={12} lg={7}>
          <Paper sx={{ boxShadow: "0px 0px 0px" }}>
            <Grid container padding={"20px"} direction="column">
              <Grid
                container
                justifyContent={"space-between"}
                alignItems="center"
              >
                <Typography variant="h5">
                  {t("emergency.labels.today_visits", {
                    defaultValue: "Today's Emergency Visits",
                  })}
                </Typography>
              </Grid>
              <Divider sx={{ mt: 1, width: "100%" }} />

              {/* Filters */}
              <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search name/phone"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <TextField
                  size="small"
                  type="date"
                  label="From Date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To Date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Autocomplete
                  size="small"
                  options={attendingOptions}
                  value={attendingValue}
                  onChange={(e, val) => setAttendingValue(val)}
                  inputValue={attendingInput}
                  onInputChange={(e, val) => setAttendingInput(val)}
                  getOptionLabel={(o) =>
                    (
                      (o?.first_name || "") +
                      " " +
                      (o?.last_name || "")
                    ).trim() ||
                    o?.username ||
                    ""
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Attending Doctor"
                      placeholder="Search doctor"
                    />
                  )}
                  sx={{ minWidth: 200 }}
                />
                <TextField
                  size="small"
                  select
                  label={t("emergency.labels.triage")}
                  onChange={(e) =>
                    setParams((p) => ({
                      ...p,
                      triage_level: e.target.value || undefined,
                    }))
                  }
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={1}>Red</MenuItem>
                  <MenuItem value={2}>Yellow</MenuItem>
                  <MenuItem value={3}>Green</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  select
                  label={t("emergency.labels.disposition")}
                  onChange={(e) =>
                    setParams((p) => ({
                      ...p,
                      disposition: e.target.value || undefined,
                    }))
                  }
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={1}>Discharge</MenuItem>
                  <MenuItem value={2}>Admit IPD</MenuItem>
                  <MenuItem value={3}>Admit ICU</MenuItem>
                  <MenuItem value={4}>Referral</MenuItem>
                  <MenuItem value={5}>Death</MenuItem>
                </TextField>
                <Button
                  variant="contained"
                  onClick={() => navigate("/emergency/new")}
                  sx={{ borderRadius: 2, px: 3 }}
                >
                  {t("emergency.buttons.new_visit", {
                    defaultValue: "New Visit",
                  })}
                </Button>
              </Box>

              {error ? (
                <Alert severity="error" sx={{ mt: 4 }}>
                  {t("emergency.errors.load_failed", {
                    defaultValue:
                      "Failed to load emergency visits. Please try again.",
                  })}
                </Alert>
              ) : isFetching ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  mt={4}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer sx={{ mt: 4 }}>
                    <Table
                      sx={{ minWidth: "100%" }}
                      aria-label="emergency visits table"
                    >
                      <TableHead style={{ backgroundColor: "#f2f2f2" }}>
                        <TableRow>
                          <TableCell align="center">
                            {t("emergency.labels.id", { defaultValue: "ID" })}
                          </TableCell>
                          <TableCell align="center">
                            {t("emergency.labels.arrival", {
                              defaultValue: "Arrival",
                            })}
                          </TableCell>
                          <TableCell align="center">
                            {t("emergency.labels.patient_walkin", {
                              defaultValue: "Patient/Walk-in",
                            })}
                          </TableCell>
                          <TableCell align="center">
                            {t("emergency.labels.triage", {
                              defaultValue: "Triage",
                            })}
                          </TableCell>
                          <TableCell align="center">
                            {t("emergency.labels.disposition", {
                              defaultValue: "Disposition",
                            })}
                          </TableCell>
                          <TableCell align="center">
                            {t("emergency.labels.actions", {
                              defaultValue: "Actions",
                            })}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                        {rows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              {t("emergency.no_visits", {
                                defaultValue: "No Emergency Visits",
                              })}
                            </TableCell>
                          </TableRow>
                        ) : (
                          rows.map((r) => (
                            <TableRow
                              key={r.id}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell
                                align="center"
                                component="th"
                                scope="row"
                              >
                                {r.id}
                              </TableCell>
                              <TableCell align="center">
                                {r.arrival_time
                                  ? new Date(r.arrival_time).toLocaleString()
                                  : "-"}
                              </TableCell>
                              <TableCell align="center">
                                {r.patient
                                  ? (() => {
                                      // Use patient_details from backend if available
                                      if (r.patient_details) {
                                        const { firstname, lastname } =
                                          r.patient_details;
                                        if (firstname || lastname) {
                                          return `${firstname || ""} ${
                                            lastname || ""
                                          }`.trim();
                                        }
                                        return `Patient #${r.patient_details.id}`;
                                      }

                                      // Fallback to old logic for backward compatibility
                                      if (typeof r.patient === "string") {
                                        return r.patient; // If it's already a string/name
                                      }

                                      // Check for firstname/lastname (common in this system)
                                      if (
                                        r.patient.firstname ||
                                        r.patient.lastname
                                      ) {
                                        const fullName = `${
                                          r.patient.firstname || ""
                                        } ${r.patient.lastname || ""}`.trim();
                                        if (fullName) return fullName;
                                      }

                                      // Check for first_name/last_name
                                      if (
                                        r.patient.first_name ||
                                        r.patient.last_name
                                      ) {
                                        const fullName = `${
                                          r.patient.first_name || ""
                                        } ${r.patient.last_name || ""}`.trim();
                                        if (fullName) return fullName;
                                      }

                                      if (r.patient.name) {
                                        return r.patient.name;
                                      }

                                      if (r.patient.username) {
                                        return r.patient.username;
                                      }

                                      if (r.patient.id) {
                                        return `Patient #${r.patient.id}`;
                                      }

                                      // If patient object exists but we can't find a name
                                      return "Linked Patient";
                                    })()
                                  : r.walkin_name || "Walk-in"}
                              </TableCell>
                              <TableCell align="center">
                                <span
                                  style={{
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    color: "white",
                                    backgroundColor:
                                      r.triage_level === 1
                                        ? "#d32f2f"
                                        : r.triage_level === 2
                                        ? "#ed6c02"
                                        : "#2e7d32",
                                  }}
                                >
                                  {triageLabel(r.triage_level)}
                                </span>
                              </TableCell>
                              <TableCell align="center">
                                {dispositionLabel(r.disposition)}
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => navigate(`/emergency/${r.id}`)}
                                >
                                  {t("emergency.actions.view", {
                                    defaultValue: "View",
                                  })}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination Controls */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={4}
                    mb={2}
                    flexWrap="wrap"
                    gap={2}
                  >
                    {/* Rows Per Page Selector */}
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" mr={1}>
                        {t("emergency.rows_per_page", {
                          defaultValue: "Rows Per Page",
                        })}
                        :
                      </Typography>
                      <FormControl variant="standard" sx={{ minWidth: 80 }}>
                        <Select
                          value={rowsPerPage}
                          onChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                          }}
                          label={t("emergency.rows_per_page", {
                            defaultValue: "Rows Per Page",
                          })}
                        >
                          <MenuItem value={5}>5</MenuItem>
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={25}>25</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Pagination Component */}
                    <Pagination
                      count={Math.ceil(total / rowsPerPage)}
                      page={page + 1}
                      onChange={(event, value) => setPage(value - 1)}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Box>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
}
